import React, { useState, useEffect, useCallback } from 'react';
import { coachingAnalytics } from '../../api';
import '../Homework/Homework.css';

const REFRESH_MS = 2 * 60 * 1000;

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const dateText = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
};
const tomorrowKey = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};

// One day of the team: totals, then who teaches when and where. Coaches with no lessons are listed as chips.
const DayBlock = ({ day }) => {
  const teaching = day.coaches.filter(c => c.lessons > 0).sort((a, b) => (a.first || '').localeCompare(b.first || ''));
  return (
    <>
      <div className="br-tiles">
        <div className="br-tile"><div className="br-num">{day.lessons}</div><div className="br-lab">lessons</div></div>
        <div className="br-tile"><div className="br-num">{day.hours}</div><div className="br-lab">hours</div></div>
        <div className="br-tile"><div className="br-num">{day.coachesTeaching}/{day.coaches.length}</div><div className="br-lab">coaches teaching</div></div>
      </div>
      {teaching.map(c => (
        <div key={c.name} className="br-row">
          <div>
            <div className="br-name">{c.name}</div>
            {c.venues.length > 0 && <div className="br-meta">{c.venues.join(' · ')}</div>}
          </div>
          <div className="br-right">
            {plural(c.lessons, 'lesson', 'lessons')}
            <small>{c.first}–{c.last} · {c.hours} h</small>
          </div>
        </div>
      ))}
      {day.lessons === 0 && <div className="br-meta">No lessons in the team's calendars.</div>}
      {day.free.length > 0 && day.lessons > 0 && (
        <>
          <div className="br-section">No lessons</div>
          <div className="br-free">{day.free.map(n => <span key={n} className="br-chip">{n}</span>)}</div>
        </>
      )}
    </>
  );
};

// Head coach: the morning brief (how today looks) and the night brief (how today went + how tomorrow looks).
// The same text arrives as a notification at 07:00 and 21:00 Singapore time.
const HeadcoachBriefCard = () => {
  const [brief, setBrief] = useState(null);
  const [view, setView] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback((refresh = false) => {
    return coachingAnalytics.getBrief(undefined, refresh)
      .then(res => {
        setBrief(res.data.brief);
        setError('');
        setView(prev => {
          if (prev) return prev;
          let wanted = '';
          try { wanted = sessionStorage.getItem('focusBrief') || ''; sessionStorage.removeItem('focusBrief'); } catch (e) { /* ignore */ }
          return wanted === 'morning' || wanted === 'night' ? wanted : res.data.type;
        });
      })
      .catch(() => setError('Could not load the brief. Pull down to try again.'));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Tapping the brief notification while the app is open: show the brief it was about
  useEffect(() => {
    const onFocus = () => {
      let wanted = '';
      try { wanted = sessionStorage.getItem('focusBrief') || ''; sessionStorage.removeItem('focusBrief'); } catch (e) { /* ignore */ }
      if (wanted === 'morning' || wanted === 'night') setView(wanted);
      load(true);
    };
    window.addEventListener('focusBrief', onFocus);
    return () => window.removeEventListener('focusBrief', onFocus);
  }, [load]);

  const send = async () => {
    setSending(true);
    setNotice('');
    try {
      await coachingAnalytics.sendBrief(view);
      setNotice('Sent to your phone as a notification');
    } catch (err) {
      setNotice('Could not send it. Try again.');
    } finally {
      setSending(false);
      setTimeout(() => setNotice(''), 4000);
    }
  };

  if (!brief) {
    return <div className="br-card"><div className="hw-sub">{error || 'Loading the brief…'}</div></div>;
  }

  const w = brief.waiting;
  const night = view === 'night';

  return (
    <div className="br-card">
      <div className="br-top">
        <h3 className="br-title">{night ? '🌙 Night brief' : '☀️ Morning brief'}</h3>
        <div className="br-seg" role="group" aria-label="Brief">
          <button type="button" aria-pressed={!night} onClick={() => setView('morning')}>Morning</button>
          <button type="button" aria-pressed={night} onClick={() => setView('night')}>Night</button>
        </div>
      </div>

      {night ? (
        <>
          <div className="br-section" style={{ marginTop: 0 }}>Today · {dateText(brief.date)}</div>
          <div className="br-meta" style={{ fontSize: 14, color: 'inherit' }}>
            {brief.today.lessons === 0
              ? 'No lessons were taught.'
              : `${plural(brief.today.lessons, 'lesson', 'lessons')} · ${brief.today.hours} h by ${plural(brief.today.coachesTeaching, 'coach', 'coaches')}.`}
            {brief.homeworkDoneToday > 0 && ` ${plural(brief.homeworkDoneToday, 'homework task', 'homework tasks')} ticked off by players.`}
          </div>
          <div className="br-section">Tomorrow · {dateText(tomorrowKey(brief.date))}</div>
          <DayBlock day={brief.tomorrow} />
        </>
      ) : (
        <>
          <div className="br-section" style={{ marginTop: 0 }}>Today · {dateText(brief.date)}</div>
          <DayBlock day={brief.today} />
        </>
      )}

      {(w.bookingRequests > 0 || w.unansweredTasks > 0) && (
        <div className="br-wait">
          {w.bookingRequests > 0 && <span className="br-chip">{plural(w.bookingRequests, 'booking request', 'booking requests')} waiting</span>}
          {w.unansweredTasks > 0 && <span className="br-chip">{plural(w.unansweredTasks, 'task', 'tasks')} unanswered</span>}
        </div>
      )}
      {brief.calendarProblems.length > 0 && (
        <div className="br-warn">⚠️ No calendar to read for: {brief.calendarProblems.join(', ')}</div>
      )}

      <button type="button" className="hw-ghost br-send" onClick={send} disabled={sending}>
        {sending ? 'Sending…' : '🔔 Send me this as a notification'}
      </button>
      {notice && <div className="br-meta" style={{ marginTop: 6 }}>{notice}</div>}
    </div>
  );
};

export default HeadcoachBriefCard;
